import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Root } from './styles';
import { Button } from '../Button/Button';
import { cx } from '@linaria/core';
import { Typo } from '../primitives/Typo';
import { AnimationType, ContextMenu } from '../ContextMenu/ContextMenu';
import Icon from '../Icon/Icon';
import { doNothing } from '../misc';
function useItems(items, activeIndex, setActiveIndex, hasActiveCheckmarkIcon, hasActiveButtons) {
    return useMemo(() => {
        if (items) {
            return items.map((item, i) => {
                return {
                    isActive: hasActiveButtons ? i === activeIndex : false,
                    ...item,
                    iconRight: hasActiveCheckmarkIcon && i === activeIndex ? React.createElement(Icon, { name: "check" }) : item.iconRight,
                    onClick: (event) => {
                        item.onClick?.(event);
                        setActiveIndex(i);
                    },
                    tabIndex: -1,
                    accessibility: 'menuitemradio',
                    ariaChecked: i === activeIndex,
                };
            });
        }
        else {
            return [];
        }
    }, [items, activeIndex]);
}
export function checkForActiveItem(items) {
    const index = items?.findIndex((item) => item['isActive']);
    if (index === -1) {
        return null;
    }
    else {
        return index;
    }
}
function getAnimationType(hasArrow, selectButton) {
    if (selectButton.id?.startsWith('toolbarAccountButton')) {
        return AnimationType.RIGHT_TO_LEFT;
    }
    if (!hasArrow) {
        return AnimationType.TOP_TO_BOTTOM;
    }
    return AnimationType.DEFAULT;
}
export function Dropdown({ selectButton, items, hasFixedSelectButtonText, hasActiveCheckmarkIcon = true, hasActiveButtons = true, isScrollingToActiveElement = true, isFullwidth = true, hasDisabledAnimations, onChange = doNothing, appendTo, hasArrow = false, hasSelectButtonArrow = true, id, isOpen = false, }) {
    /** Variables */
    const [activeIndex, setActiveIndex] = useState(() => checkForActiveItem(items));
    const [open, setOpen] = useState(isOpen);
    const ContextMenuRef = useRef(null);
    const onOpenMemo = useCallback(() => setOpen(true), []);
    const onCloseMemo = useCallback(() => setOpen(false), []);
    const contextMenuItems = useItems(items, activeIndex, setActiveIndex, hasActiveCheckmarkIcon, hasActiveButtons);
    const animationType = getAnimationType(hasArrow, selectButton);
    useEffect(() => {
        if (isScrollingToActiveElement && open && activeIndex && ContextMenuRef.current) {
            const list = ContextMenuRef.current.querySelector('ul:first-of-type');
            const activeElement = list?.children?.[activeIndex];
            if (list && activeElement) {
                list.scrollTop = activeElement.offsetTop - activeElement.offsetHeight * 2 - 8; // offsetHeight * 2 because we want the active Element to be at the middle not at the top
            }
        }
    }, [open]);
    useEffect(() => {
        setActiveIndex(checkForActiveItem(items));
    }, [items]);
    useEffect(() => {
        onChange(activeIndex);
    }, [activeIndex]);
    /** JSX */
    return (React.createElement("div", { className: cx(Root.base, isFullwidth && Root.isFullwidth, Typo.base, Typo.label01) },
        React.createElement(ContextMenu, { items: contextMenuItems, hasDisabledAnimations: hasDisabledAnimations, onOpen: onOpenMemo, onClose: onCloseMemo, appendTo: isFullwidth ? null : appendTo, isFullwidth: isFullwidth, hasArrow: animationType === 'default', animationType: animationType, contextMenuRef: ContextMenuRef, id: id, isOpen: isOpen },
            React.createElement(Button, { ...selectButton, text: !hasFixedSelectButtonText && typeof activeIndex === 'number'
                    ? contextMenuItems[activeIndex]?.text
                    : selectButton.text, iconRight: hasSelectButtonArrow && React.createElement(Icon, { name: "chevronDown" }), toggleIconRight: hasSelectButtonArrow && React.createElement(Icon, { name: "chevronUp" }), isToggle: hasSelectButtonArrow && open, hasDisabledAnimations: hasDisabledAnimations, ariaExpanded: open }))));
}
export default React.memo(Dropdown);
